import React, { useMemo } from "react";
import { Stack, Typography, Button } from "@mui/material";
import { isGuidedTourNarrationMuted } from "./useGuidedTourNarration";
import GuidedTourListenButton from "./GuidedTourListenButton";
import {
  GUIDED_TOUR_ACTION_BUTTON_SX,
  GUIDED_TOUR_POPOVER_CONTENT_SPACING,
} from "./guidedTourPopoverStyles";

/**
 * Guided tour popup footer: Listen (desktop) + step counter + navigation.
 * Listen narrates the current step title/content (same as the popover text).
 */
export default function GuidedTourStepFooter({
  stepIndex,
  totalSteps,
  currentStep,
  onBack,
  onNext,
  nextLabel,
  narrationEnabled,
  speak,
  stop,
  isSpeaking,
}) {
  const speechSupported =
    typeof window !== "undefined" && "speechSynthesis" in window;

  const narrationText = useMemo(() => {
    if (currentStep?.title && currentStep?.content) {
      return `${currentStep.title}. ${currentStep.content}`;
    }
    if (currentStep?.content) return currentStep.content;
    if (currentStep?.title) return currentStep.title;
    return null;
  }, [currentStep]);

  const handleListen = () => {
    if (narrationText) speak(narrationText);
  };

  const resolvedNextLabel =
    nextLabel ??
    (stepIndex === totalSteps - 1 ? "Finish" : "Next");

  return (
    <Stack spacing={GUIDED_TOUR_POPOVER_CONTENT_SPACING}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={1}
      >
        <GuidedTourListenButton
          text={narrationText}
          isSpeaking={isSpeaking}
          isMuted={isGuidedTourNarrationMuted()}
          speechSupported={speechSupported}
          narrationEnabled={narrationEnabled}
          onListen={handleListen}
          onStop={stop}
        />
        <Typography variant="caption" color="text.secondary">
          {stepIndex + 1} / {totalSteps}
        </Typography>
      </Stack>

      <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent="flex-end">
        {stepIndex > 0 && (
          <Button size="small" variant="outlined" onClick={onBack} sx={GUIDED_TOUR_ACTION_BUTTON_SX}>
            Back
          </Button>
        )}
        <Button size="small" variant="contained" onClick={onNext} sx={GUIDED_TOUR_ACTION_BUTTON_SX}>
          {resolvedNextLabel}
        </Button>
      </Stack>
    </Stack>
  );
}
